import { type ComponentType, useEffect } from 'react';

import { useChartMenuContext } from '../ChartMenuContext';
import { deriveSharedChartType } from './deriveSharedChartType';

export type SharableChartProps<DataT, CPropsT extends object = object> = {
  Component: ComponentType<{ data: DataT } & CPropsT>;
  data: DataT;
} & CPropsT;

/**
 * Wraps a chart component and registers its Replots type and props with the
 * chart menu for PPT export. Share links are built from the snapshot the
 * `withShareCapture` HOC on each replots export captures, not from here.
 */
export function SharableChart<DataT, CPropsT extends object = object>(
  props: SharableChartProps<DataT, CPropsT>
) {
  const { Component, data, ...rest } = props;
  const chartProps = rest as CPropsT;
  const { setShareData } = useChartMenuContext();

  const chartType = deriveSharedChartType(Component);

  useEffect(() => {
    if (!chartType) return;

    setShareData({
      chartType,
      chartProps: chartProps as Record<string, unknown>,
    });
  }, [chartType, chartProps, setShareData]);

  return <Component data={data} {...chartProps} />;
}
